/**
 * /knowledge/new e /knowledge/$knowledgeId/edit — editor de conhecimento:
 * título grande sem borda, metadados (Pasta/Tags/Público), TipTap e anexos.
 * Duas rotas renderizam wrappers finos sobre o mesmo KnowledgeEditorScreen.
 */
import { useRef, useState, type KeyboardEvent } from 'react';
import { z } from 'zod';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Folder, Globe, Paperclip, Tag, X } from 'lucide-react';
import { MAX_UPLOAD_BYTES, type KnowledgeDetail } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import {
  errorMessage,
  knowledgeDetailOptions,
  knowledgeFoldersOptions,
} from '@/features/knowledge/knowledge-queries';
import {
  useCreateKnowledge,
  useDeleteKnowledgeFile,
  useUpdateKnowledge,
  useUploadKnowledgeFile,
} from '@/features/knowledge/knowledge-mutations';
import { formatFileSize } from '@/features/knowledge/knowledge-format';
import { KnowledgeEditor, useKnowledgeEditor } from '@/features/knowledge/knowledge-editor';

export const knowledgeNewSearchSchema = z.object({
  folder: z.coerce.number().int().positive().optional(),
});

const newRouteApi = getRouteApi('/_authenticated/knowledge/new');
const editRouteApi = getRouteApi('/_authenticated/knowledge/$knowledgeId/edit');

export function KnowledgeNewPage() {
  const { folder } = newRouteApi.useSearch();
  return <KnowledgeEditorScreen detail={null} initialFolderId={folder ?? null} />;
}

export function KnowledgeEditPage() {
  const { knowledgeId } = editRouteApi.useParams();
  const id = Number(knowledgeId);
  const detail = useQuery(knowledgeDetailOptions(id));

  if (detail.isPending) {
    return (
      <div aria-busy className="mx-auto w-full max-w-3xl space-y-4">
        <Skeleton className="h-3.5 w-64" />
        <Skeleton className="h-9 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="rounded-lg border bg-card shadow-xs">
        <EmptyState message={`Erro ao carregar conhecimento: ${errorMessage(detail.error)}`} />
      </div>
    );
  }
  // key: remonta o screen (estado + editor) quando trocar de artigo.
  return (
    <KnowledgeEditorScreen key={id} detail={detail.data} initialFolderId={detail.data.folderId} />
  );
}

// ── Tags como chips (vírgula/Enter adiciona; X remove) ──────────────────────
function TagsInput({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const value = draft.trim().replace(/,+$/, '').trim();
    setDraft('');
    if (!value || tags.includes(value) || tags.length >= 20) return;
    onChange([...tags, value]);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div className="flex min-h-8 flex-1 flex-wrap items-center gap-1.5">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded-sm border border-border bg-muted px-2 py-0.5 text-xs font-medium"
        >
          {tag}
          <button
            type="button"
            aria-label={`Remover tag ${tag}`}
            onClick={() => onChange(tags.filter((t) => t !== tag))}
            className="text-muted-foreground transition-colors hover:text-destructive"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={tags.length === 0 ? 'Digite e pressione vírgula ou Enter' : ''}
        aria-label="Adicionar tag"
        className="h-8 min-w-40 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
      />
    </div>
  );
}

// ── Anexos (somente no modo edição) ─────────────────────────────────────────
function AttachmentsSection({ detail }: { detail: KnowledgeDetail }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const upload = useUploadKnowledgeFile(detail.id);
  const removeFile = useDeleteKnowledgeFile();

  const onPick = (files: FileList | null) => {
    const file = files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setSizeError(`O arquivo excede o limite de 25 MB (${formatFileSize(file.size)}).`);
      return;
    }
    setSizeError(null);
    upload.mutate(file);
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">Anexos ({detail.files.length})</h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={upload.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="size-3.5" />
          {upload.isPending ? 'Enviando…' : 'Anexar arquivo'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => onPick(e.target.files)}
        />
      </div>
      {sizeError ? <p className="text-sm text-destructive">{sizeError}</p> : null}
      {upload.error ? (
        <p className="text-sm text-destructive">Falha no upload: {errorMessage(upload.error)}</p>
      ) : null}
      {detail.files.length > 0 ? (
        <div className="flex flex-col gap-2">
          {detail.files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2.5 rounded-md border bg-card px-3 py-2"
            >
              <Paperclip className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{file.fileName}</span>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {formatFileSize(file.size)}
              </span>
              <button
                type="button"
                aria-label={`Remover ${file.fileName}`}
                disabled={removeFile.isPending}
                onClick={() => removeFile.mutate(file.id)}
                className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-destructive disabled:opacity-40"
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

// ── Tela do editor (compartilhada entre new e edit) ─────────────────────────
function KnowledgeEditorScreen({
  detail,
  initialFolderId,
}: {
  detail: KnowledgeDetail | null;
  initialFolderId: number | null;
}) {
  const navigate = useNavigate();
  const folders = useQuery(knowledgeFoldersOptions());

  const [title, setTitle] = useState(detail?.title ?? '');
  const [folderId, setFolderId] = useState<string>(initialFolderId ? String(initialFolderId) : '');
  const [tags, setTags] = useState<string[]>(detail?.tags ?? []);
  const [isPublic, setIsPublic] = useState(detail?.isPublic ?? false);
  const editor = useKnowledgeEditor(detail?.body ?? '');

  const create = useCreateKnowledge();
  const update = useUpdateKnowledge(detail?.id ?? 0);
  const saving = create.isPending || update.isPending;
  const saveError = create.error ?? update.error;
  const canSave = title.trim().length > 0 && folderId !== '' && !saving;

  const cancel = () => {
    if (detail) {
      void navigate({ to: '/knowledge/$knowledgeId', params: { knowledgeId: String(detail.id) } });
    } else if (initialFolderId) {
      void navigate({
        to: '/knowledge/folder/$folderId',
        params: { folderId: String(initialFolderId) },
      });
    } else {
      void navigate({ to: '/knowledge' });
    }
  };

  const save = () => {
    if (!canSave) return;
    const body = {
      folderId: Number(folderId),
      title: title.trim(),
      body: editor?.getHTML() ?? '',
      tags,
      isPublic,
    };
    const goToArticle = (saved: KnowledgeDetail) =>
      void navigate({ to: '/knowledge/$knowledgeId', params: { knowledgeId: String(saved.id) } });
    if (detail) update.mutate(body, { onSuccess: goToArticle });
    else create.mutate(body, { onSuccess: goToArticle });
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4">
      {/* Breadcrumb */}
      <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/knowledge" className="hover:text-foreground">
          Conhecimentos
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-foreground">{detail ? detail.title : 'Novo conhecimento'}</span>
      </nav>

      {/* Título grande sem borda */}
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Digite um título..."
        aria-label="Título do conhecimento"
        autoFocus={!detail}
        className="w-full bg-transparent text-2xl font-semibold outline-none placeholder:text-muted-foreground/50"
      />

      {/* Metadados */}
      <div className="space-y-2">
        <div className="flex items-center gap-2.5">
          <Folder className="size-4 shrink-0 text-muted-foreground" />
          <span className="w-14 shrink-0 text-sm text-muted-foreground">Pasta:</span>
          <Select
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            required
            aria-label="Pasta do conhecimento"
            className={cn('h-8 w-64', folderId === '' && 'text-muted-foreground')}
          >
            <option value="" disabled>
              Selecione uma pasta…
            </option>
            {(folders.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.icon ? `${f.icon} ` : ''}
                {f.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-start gap-2.5">
          <Tag className="mt-2 size-4 shrink-0 text-muted-foreground" />
          <span className="mt-1.5 w-14 shrink-0 text-sm text-muted-foreground">Tags:</span>
          <TagsInput tags={tags} onChange={setTags} />
        </div>

        <div className="flex items-center gap-2.5">
          <Globe className="size-4 shrink-0 text-muted-foreground" />
          <span className="w-14 shrink-0 text-sm text-muted-foreground">Público:</span>
          <Switch
            checked={isPublic}
            onCheckedChange={setIsPublic}
            aria-label="Tornar conhecimento público"
          />
          <span className="text-[13px] text-muted-foreground">
            Gera link compartilhável sem login
          </span>
        </div>
      </div>

      {/* Editor */}
      <KnowledgeEditor editor={editor} />

      {/* Anexos: no new não há id ainda — hint; no edit, upload/lista */}
      {detail ? (
        <AttachmentsSection detail={detail} />
      ) : (
        <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <Paperclip className="size-3.5" />
          Salve para anexar arquivos.
        </p>
      )}

      {saveError ? (
        <p className="text-sm text-destructive">Erro ao salvar: {errorMessage(saveError)}</p>
      ) : null}

      {/* Rodapé */}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" size="sm" onClick={cancel}>
          Cancelar
        </Button>
        <Button type="button" size="sm" disabled={!canSave} onClick={save}>
          {saving ? 'Salvando…' : detail ? 'Salvar' : 'Criar conhecimento'}
        </Button>
      </div>
    </div>
  );
}
